import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Copy, Loader2 } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Configuração do app da Meta pela tela, em vez do .env.
//
// Antes estes valores viviam no arquivo de ambiente: trocar um deles exigia
// editar o .env do servidor, repassar a variável pelo docker-compose e
// reiniciar o contêiner. Quando um passo faltava, o painel se comportava como
// se nada estivesse configurado — sem erro, só o botão sumindo.

const CAMPOS = [
  {
    chave: "appId",
    label: "ID do app",
    dica: "O número que aparece no topo do painel em developers.facebook.com."
  },
  {
    chave: "appSecret",
    label: "Chave secreta do app",
    tipo: "password",
    dica: "Usada para conferir a assinatura de cada webhook. Deixe em branco para manter a atual."
  },
  {
    chave: "verifyToken",
    label: "Token de verificação",
    dica: "Você digita o mesmo valor no painel da Meta ao salvar a URL do webhook."
  },
  {
    chave: "configIdFacebook",
    label: "Configuração — Messenger",
    dica: "Só para apps de Login for Business. Deixe vazio se o app usa o login clássico."
  },
  {
    chave: "configIdInstagram",
    label: "Configuração — Instagram",
    dica: "Idem."
  },
  {
    chave: "configIdWhatsapp",
    label: "Configuração — WhatsApp",
    dica: "Idem."
  }
];

const MetaSettings = () => {
  const [valores, setValores] = useState({});
  const [callbackUrl, setCallbackUrl] = useState("");
  const [temSecret, setTemSecret] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(async () => {
    try {
      const { data } = await api.get("/meta/app-config");

      setValores({
        appId: data?.appId || "",
        appSecret: "",
        verifyToken: data?.verifyToken || "",
        configIdFacebook: data?.configIds?.facebook || "",
        configIdInstagram: data?.configIds?.instagram || "",
        configIdWhatsapp: data?.configIds?.whatsapp || ""
      });

      setTemSecret(Boolean(data?.temAppSecret));
      setCallbackUrl(data?.callbackUrl || "");
    } catch (err) {
      toastError(err);
    }

    setCarregando(false);
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const salvar = async () => {
    setSalvando(true);

    try {
      await api.put("/meta/app-config", valores);
      toast.success("Configuração da Meta salva.");
      await carregar();
    } catch (err) {
      toastError(err);
    }

    setSalvando(false);
  };

  const copiar = (texto) => {
    navigator.clipboard.writeText(texto);
    toast.success("Copiado.");
  };

  if (carregando) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* O que colar no painel da Meta. Montado pelo servidor a partir da URL
          real da instalação — é o erro mais comum de configuração. */}
      {callbackUrl && (
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="text-sm font-medium">URL de retorno de chamada</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Cole no painel da Meta, em Webhooks, nos produtos Messenger,
            Instagram e WhatsApp.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 truncate rounded bg-background px-2 py-1 text-xs">
              {callbackUrl}
            </code>
            <Button
              variant="outline"
              size="icon"
              onClick={() => copiar(callbackUrl)}
              aria-label="Copiar URL"
            >
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {CAMPOS.map((campo) => (
          <div key={campo.chave} className="space-y-1.5">
            <Label htmlFor={campo.chave}>{campo.label}</Label>
            <Input
              id={campo.chave}
              type={campo.tipo || "text"}
              value={valores[campo.chave] || ""}
              placeholder={
                campo.chave === "appSecret" && temSecret
                  ? "••••••••  (já configurada)"
                  : ""
              }
              onChange={(e) =>
                setValores((v) => ({ ...v, [campo.chave]: e.target.value }))
              }
            />
            <p className="text-xs text-muted-foreground">{campo.dica}</p>
          </div>
        ))}
      </div>

      <Button onClick={salvar} disabled={salvando}>
        {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
        Salvar
      </Button>
    </div>
  );
};

export default MetaSettings;
